import { MutationTree } from 'vuex';
import { ErrorDetail } from '../../types';
import { NodeGroupState, NodeGroup } from './types';
import { Node } from '../node/types';
import * as types from './mutation-types';

// 更新・削除・所属ノード追加・解除のエラーをクリアする
const resetChangesAllErrors = (state: NodeGroupState): void => {
    const updateState = state.update;
    updateState.error = false;
    updateState.errorTitle = undefined;
    updateState.errorDetails = undefined;

    const destroyState = state.destroy;
    destroyState.error = false;
    destroyState.errorTitle = undefined;
    destroyState.errorDetails = undefined;

    const addNodesState = state.addNodes;
    addNodesState.error = false;
    addNodesState.errorTitle = undefined;
    addNodesState.errorDetails = undefined;

    const removeNodesState = state.removeNodes;
    removeNodesState.error = false;
    removeNodesState.errorTitle = undefined;
    removeNodesState.errorDetails = undefined;

    const csvState = state.csv;
    csvState.error = false;
    csvState.errorCode = undefined;
    csvState.errorMsg = undefined;
};

export const mutations: MutationTree<NodeGroupState> = {
    // ノードグループ一覧系
    [types.INDEX_START](state) {
        const indexState = state.index;
        indexState.error = false;
        indexState.errorCode = undefined;
        indexState.errorMsg = undefined;
        indexState.isProcessing = true;
        indexState.isFetchSuccess = false;
    },

    [types.INDEX_SUCCESS](state, payload: { count: number; node_groups: Array<NodeGroup> }) {
        state.nodeGroupCount = payload.count;
        state.nodeGroups = payload.node_groups;

        const indexState = state.index;
        indexState.isProcessing = false;
        indexState.isFetchSuccess = true;
    },

    [types.INDEX_FAILURE](state, payload: { code: string; message: string }) {
        const indexState = state.index;
        indexState.errorCode = payload.code;
        indexState.errorMsg = payload.message;
        indexState.error = true;
        indexState.isProcessing = false;
        indexState.isFetchSuccess = false;
    },

    // SAMLサービスプロバイダのノードが紐づくノードグループ一覧系
    [types.INDEX_SAML_START](state) {
        const indexSamlState = state.indexSaml;
        indexSamlState.error = false;
        indexSamlState.errorCode = undefined;
        indexSamlState.errorMsg = undefined;
        indexSamlState.isProcessing = true;
        indexSamlState.isFetchSuccess = false;
    },

    [types.INDEX_SAML_SUCCESS](state, payload: { count: number; node_groups: Array<NodeGroup> }) {
        state.samlNodeGroupCount = payload.count;
        state.samlNodeGroups = payload.node_groups;

        const indexSamlState = state.indexSaml;
        indexSamlState.isProcessing = false;
        indexSamlState.isFetchSuccess = true;
    },

    [types.INDEX_SAML_FAILURE](state, payload: { code: string; message: string }) {
        const indexSamlState = state.indexSaml;
        indexSamlState.errorCode = payload.code;
        indexSamlState.errorMsg = payload.message;
        indexSamlState.error = true;
        indexSamlState.isProcessing = false;
        indexSamlState.isFetchSuccess = false;
    },

    // ノードグループ作成系
    [types.NEW_NODE_GROUP](state) {
        const createState = state.create;
        createState.error = false;
        createState.errorTitle = undefined;
        createState.errorDetails = undefined;
        createState.isCreated = false;
    },

    [types.CREATE_START](state) {
        const createState = state.create;
        createState.error = false;
        createState.errorTitle = undefined;
        createState.errorDetails = undefined;
        createState.isProcessing = true;
    },

    [types.CREATE_SUCCESS](state) {
        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = true;
    },

    [types.CREATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const createState = state.create;
        createState.errorTitle = payload.errorTitle;
        createState.errorDetails = payload.errorDetails;
        createState.error = true;
        createState.isProcessing = false;
    },

    // ノードグループ詳細情報参照系
    [types.SHOW_START](state) {
        resetChangesAllErrors(state);
        const showState = state.show;
        showState.error = false;
        showState.errorCode = undefined;
        showState.errorMsg = undefined;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](state, payload: { group: NodeGroup; node: Array<Node> }) {
        state.nodeGroupDetails.group = payload.group;
        state.nodeGroupDetails.nodes = payload.node.length > 0 ? payload.node : [];

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // ノードグループ更新系
    [types.UPDATE_START](state) {
        resetChangesAllErrors(state);
        const updateState = state.update;
        updateState.isProcessing = true;
    },

    [types.UPDATE_SUCCESS](state) {
        const updateState = state.update;
        updateState.isProcessing = false;
    },

    [types.UPDATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updateState = state.update;
        updateState.errorTitle = payload.errorTitle;
        updateState.errorDetails = payload.errorDetails;
        updateState.error = true;
        updateState.isProcessing = false;
    },

    // ノードグループ削除系
    [types.DESTROY_START](state) {
        resetChangesAllErrors(state);
        const destroyState = state.destroy;
        destroyState.isProcessing = true;
    },

    [types.DESTROY_SUCCESS](state) {
        const destroyState = state.destroy;
        destroyState.isProcessing = false;
    },

    [types.DESTROY_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const destroyState = state.destroy;
        destroyState.errorTitle = payload.errorTitle;
        destroyState.errorDetails = payload.errorDetails;
        destroyState.error = true;
        destroyState.isProcessing = false;
    },

    // 所属ノード追加系
    [types.NEW_NODES](state) {
        const addNodesState = state.addNodes;
        addNodesState.error = false;
        addNodesState.errorTitle = undefined;
        addNodesState.errorDetails = undefined;
    },

    [types.ADD_NODES_START](state) {
        resetChangesAllErrors(state);
        const addNodesState = state.addNodes;
        addNodesState.isProcessing = true;
    },

    [types.ADD_NODES_SUCCESS](state) {
        const addNodesState = state.addNodes;
        addNodesState.isProcessing = false;
    },

    [types.ADD_NODES_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const addNodesState = state.addNodes;
        addNodesState.errorTitle = payload.errorTitle;
        addNodesState.errorDetails = payload.errorDetails;
        addNodesState.error = true;
        addNodesState.isProcessing = false;
    },

    // 所属ノード削減系
    [types.REMOVE_NODES_START](state) {
        resetChangesAllErrors(state);
        const removeNodesState = state.removeNodes;
        removeNodesState.isProcessing = true;
    },

    [types.REMOVE_NODES_SUCCESS](state) {
        const removeNodesState = state.removeNodes;
        removeNodesState.isProcessing = false;
    },

    [types.REMOVE_NODES_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const removeNodesState = state.removeNodes;
        removeNodesState.errorTitle = payload.errorTitle;
        removeNodesState.errorDetails = payload.errorDetails;
        removeNodesState.error = true;
        removeNodesState.isProcessing = false;
    },

    // CSV出力系
    [types.CSV_OUTPUT_START](state) {
        resetChangesAllErrors(state);
        const csvState = state.csv;
        csvState.isProcessing = true;
    },

    [types.CSV_OUTPUT_SUCCESS](state) {
        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CSV_OUTPUT_FAILURE](state, payload: { code: string; message: string }) {
        const csvState = state.csv;
        csvState.errorCode = payload.code;
        csvState.errorMsg = payload.message;
        csvState.error = true;
        csvState.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const indexState = state.index;
        indexState.isProcessing = false;
        indexState.isFetchSuccess = false;

        const indexSamlState = state.indexSaml;
        indexSamlState.isProcessing = false;
        indexSamlState.isFetchSuccess = false;

        const createState = state.create;
        createState.isProcessing = false;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;

        const updateState = state.update;
        updateState.isProcessing = false;

        const destroyState = state.destroy;
        destroyState.isProcessing = false;

        const addNodesState = state.addNodes;
        addNodesState.isProcessing = false;

        const removeNodesState = state.removeNodes;
        removeNodesState.isProcessing = false;

        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CLEAR_ALL](state) {
        resetChangesAllErrors(state);
        state.nodeGroups = undefined;
        state.nodeGroupCount = 0;
        state.nodeGroupDetails.group = undefined;
        state.nodeGroupDetails.nodes = undefined;
        state.samlNodeGroups = undefined;
        state.samlNodeGroupCount = 0;
    },
};
